/**
 * Database backups API client (admin-only on the server side).
 */

import { API_BASE } from '../config/apiBase';
import { formatDateTimePT } from '../utils/timezone';

export const BACKUP_TAGS = ['hourly', 'daily', 'weekly', 'monthly'] as const;
export type BackupTag = (typeof BACKUP_TAGS)[number];

export interface BackupEntry {
  tag: BackupTag;
  name: string;
  size: number;
  createdAt: string;
  /**
   * 'db' = pg_dump database dump; 'uploads' = tarball of on-disk upload files
   * (attachments/photos); 'drive' = tarball of File Drive files.
   */
  kind: 'db' | 'uploads' | 'drive';
}

export interface BackupListResponse {
  backups: BackupEntry[];
  retention: Record<BackupTag, number>;
  tags: readonly BackupTag[];
  running: Record<BackupTag, boolean>;
  scheduleEnabled: boolean;
  storagePath: string;
}

async function parseError(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string };
    return data.error || fallback;
  } catch {
    return fallback;
  }
}

export async function fetchBackups(adminUserId: number): Promise<BackupListResponse> {
  const res = await fetch(
    `${API_BASE}/api/admin/backups?admin_user_id=${encodeURIComponent(adminUserId)}`
  );
  if (!res.ok) throw new Error(await parseError(res, 'Failed to load backups.'));
  return (await res.json()) as BackupListResponse;
}

export async function runBackupNow(adminUserId: number, tag: BackupTag): Promise<BackupEntry> {
  const res = await fetch(`${API_BASE}/api/admin/backups/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ admin_user_id: adminUserId, tag }),
  });
  if (!res.ok) throw new Error(await parseError(res, 'Backup failed.'));
  const data = (await res.json()) as { entry: BackupEntry };
  return data.entry;
}

export async function deleteBackup(
  adminUserId: number,
  tag: BackupTag,
  name: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/admin/backups?admin_user_id=${encodeURIComponent(adminUserId)}` +
      `&tag=${encodeURIComponent(tag)}&name=${encodeURIComponent(name)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error(await parseError(res, 'Failed to delete backup.'));
}

export function backupDownloadUrl(adminUserId: number, tag: BackupTag, name: string): string {
  return (
    `${API_BASE}/api/admin/backups/download?admin_user_id=${encodeURIComponent(adminUserId)}` +
    `&tag=${encodeURIComponent(tag)}&name=${encodeURIComponent(name)}`
  );
}

export function formatBackupSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export function formatBackupTimestamp(iso: string): string {
  return formatDateTimePT(iso, {}, iso);
}

export const BACKUP_KIND_LABELS: Record<BackupEntry['kind'], string> = {
  db: 'Database',
  uploads: 'Uploads',
  drive: 'File Drive',
};

export const BACKUP_KIND_TITLES: Record<BackupEntry['kind'], string> = {
  db: 'PostgreSQL database dump',
  uploads: 'Uploaded files (attachments & photos stored on disk)',
  drive: 'File Drive files stored on disk',
};

export const BACKUP_TAG_LABELS: Record<BackupTag, string> = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};
